import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight, Lock } from 'lucide-react'
import { isSubscriptionActive } from '@/shared/domain'
import { cn } from '@/shared/ui/cn'
import { LoadingSpinner } from '@/shared/ui/components/loading-spinner'
import { useOrgContext } from '@/shared/ui/hooks'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/shared/ui/primitives'
import { SidebarFeedbackCta } from '@/modules/feedback/ui/components'
import { useNavBadges, useNavPrefetch } from '@/shared/ui/providers'
import type { NavEntry } from './sidebar-nav'
import { SubscriptionRequiredDialog } from './subscription-required-dialog'
import { useVisibleNavEntries } from './use-visible-nav-entries'

function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count)
}

const flatLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors w-full',
    isActive ? 'bg-accent text-primary' : 'text-sidebar-foreground hover:bg-accent',
  )

function isEntryActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

export function AppSidebar() {
  const { entries, isLoading } = useVisibleNavEntries()
  const { orgId, subscriptionStatus } = useOrgContext()
  const { setOpenMobile } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const badgesByNavId = useNavBadges()
  const prefetch = useNavPrefetch()
  const [lockedFeatureLabel, setLockedFeatureLabel] = useState<string | null>(null)

  const subscriptionLocked = orgId !== null && !isSubscriptionActive(subscriptionStatus ?? null)

  function handleLockedEntryClick(label: string) {
    setOpenMobile(false)
    setLockedFeatureLabel(label)
  }

  function handleResubscribe() {
    setLockedFeatureLabel(null)
    if (orgId) navigate(`/resubscribe/${orgId}`)
  }

  function renderLockedEntry(entry: NavEntry) {
    const Icon = entry.icon
    return (
      <SidebarMenuItem key={entry.id}>
        <button
          type="button"
          onClick={() => handleLockedEntryClick(entry.label)}
          className={flatLinkClass({ isActive: false })}
        >
          <Icon className="size-5 shrink-0" aria-hidden="true" />
          <span>{entry.label}</span>
          <Lock className="ml-auto size-4 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </SidebarMenuItem>
    )
  }

  function renderFlatEntry(entry: NavEntry) {
    if (!entry.to) return null
    if (subscriptionLocked && entry.id !== 'dashboard') return renderLockedEntry(entry)
    const Icon = entry.icon
    const badgeCount = badgesByNavId[entry.id] ?? 0
    return (
      <SidebarMenuItem key={entry.id}>
        <NavLink
          to={entry.to}
          onClick={() => setOpenMobile(false)}
          onMouseEnter={() => prefetch(entry.id)}
          className={flatLinkClass}
        >
          {({ isActive }) => (
            <>
              <Icon className={cn('size-5 shrink-0', isActive && 'text-primary')} aria-hidden="true" />
              <span>{entry.label}</span>
              {badgeCount > 0 ? (
                <span
                  aria-label={`${badgeCount} unread`}
                  className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-2xs font-semibold text-primary-foreground"
                >
                  {formatBadgeCount(badgeCount)}
                </span>
              ) : null}
            </>
          )}
        </NavLink>
      </SidebarMenuItem>
    )
  }

  function renderGroupEntry(entry: NavEntry) {
    if (subscriptionLocked) return renderLockedEntry(entry)
    const Icon = entry.icon
    const isGroupActive =
      entry.children?.some((child) => child.to && isEntryActive(location.pathname, child.to)) ?? false
    return (
      <Collapsible key={entry.id} defaultOpen={isGroupActive} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger
            className={cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors',
              isGroupActive ? 'text-primary' : 'text-sidebar-foreground hover:bg-accent',
            )}
          >
            <Icon className={cn('size-5 shrink-0', isGroupActive && 'text-primary')} aria-hidden="true" />
            <span>{entry.label}</span>
            <ChevronRight className="ml-auto size-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-90" aria-hidden="true" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub className="mr-0 pr-0">
              {entry.children?.map((child) => {
                if (!child.to) return null
                const ChildIcon = child.icon
                return (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={location.pathname === child.to}
                      className="h-auto w-full gap-3 py-2.5 pr-3 text-base font-medium"
                    >
                      <NavLink
                        to={child.to}
                        onClick={() => setOpenMobile(false)}
                        onMouseEnter={() => prefetch(child.id)}
                      >
                        <ChildIcon className="size-4 shrink-0" aria-hidden="true" />
                        <span>{child.label}</span>
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                )
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  return (
    <>
    <Sidebar>
      <SidebarHeader className="items-start px-5 py-6">
        <img
          src="/blindbook-logo.svg"
          alt="BlindBook"
          className="h-12 w-auto dark:brightness-0 dark:invert"
        />
      </SidebarHeader>
      <SidebarContent className="px-3 py-2">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-6">
            <LoadingSpinner size="md" color="gray" label="Loading navigation" />
          </div>
        ) : (
          <SidebarMenu>
            {entries.map((entry) =>
              entry.children ? renderGroupEntry(entry) : renderFlatEntry(entry),
            )}
          </SidebarMenu>
        )}
      </SidebarContent>
      <SidebarFooter className="px-3 py-3">
        <SidebarFeedbackCta />
      </SidebarFooter>
    </Sidebar>
    <SubscriptionRequiredDialog
      open={lockedFeatureLabel !== null}
      featureLabel={lockedFeatureLabel}
      onClose={() => setLockedFeatureLabel(null)}
      onResubscribe={handleResubscribe}
    />
    </>
  )
}
